import { Avatar, AvatarInitials } from "@/components/Avatar";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/podkit/Card";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading4, Subheading } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { useMembership } from "@/hooks/use-membership";
import { TrackLocations } from "@/hooks/use-segment";
import { type PlainOrganization, useDeleteOrganization } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import { type FC, useCallback, useId, useMemo, useState } from "react";

type DeleteOrganizationModalProps = {
    org: PlainOrganization;
    onContinue: () => void;
    onClose: () => void;
};

export const DeleteOrganizationModal: FC<DeleteOrganizationModalProps> = (p: DeleteOrganizationModalProps) => {
    const { toast } = useToast();
    const confirmationID = useId();
    const [confirmationValue, setConfirmationValue] = useState<string>("");

    const deleteOrganization = useDeleteOrganization();

    const onOpenChange = (open: boolean) => {
        if (!open) {
            p.onClose();
        }
    };

    const confirmed = useMemo(() => confirmationValue === p.org.name, [confirmationValue, p.org.name]);

    const handleDeleteOrganization = useCallback(() => {
        deleteOrganization.mutate(undefined, {
            onSuccess: () => {
                toast({ title: "Organization deleted" });
                p.onContinue();
            },
            onError: (err) => {
                toast({ title: "Couldn't delete organization", description: formatError(err) });
            },
        });
    }, [p, deleteOrganization, toast]);

    return (
        <Dialog open onOpenChange={onOpenChange}>
            <DialogContent data-track-location={TrackLocations.DeleteOrganizationModal}>
                <DialogHeader>
                    <DialogTitle>Delete organization</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody className="flex flex-col gap-4">
                    <OrganizationCard org={p.org} />
                    <Text>
                        Are you sure you want to permanently delete <span className="font-bold">{p.org.name}</span>?
                    </Text>

                    <ol className="list-outside list-decimal pl-6">
                        <li className="pl-1">
                            All <b>environments</b> will be deleted and cannot be restored later.
                        </li>
                        <li className="pl-1">
                            All <b>members</b> will be lose access to this organization and environments.
                        </li>
                    </ol>

                    <Label htmlFor={confirmationID}>
                        Type <span className="font-bold text-content-primary">{p.org.name}</span> to confirm
                    </Label>
                    <Input
                        id={confirmationID}
                        type="text"
                        name="confirmation_value"
                        placeholder="Type to confirm"
                        value={confirmationValue}
                        onChange={(e) => setConfirmationValue(e.target.value)}
                    />
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            disabled={deleteOrganization.isPending}
                            onClick={p.onClose}
                            data-tracking-id="cancel-delete-organization-delete-organization-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        autoFocus={true}
                        disabled={!confirmed}
                        loading={deleteOrganization.isPending}
                        onClick={handleDeleteOrganization}
                        data-tracking-id="confirm-delete-organization-delete-organization-modal"
                    >
                        Yes, Delete Organization
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const OrganizationCard: FC<{ org: PlainOrganization }> = (p) => {
    const cns = "inline-flex w-full items-center justify-start gap-2 border-0 rounded-xl p-2 bg-surface-secondary";
    const { membership } = useMembership();
    if (!membership) {
        return <Card className={cns} />;
    }
    return (
        <Card className={cns}>
            <Avatar size={24}>
                <AvatarInitials name={p.org.name} size={24} />
            </Avatar>
            <div>
                <Heading4>{p.org.name}</Heading4>
                <Subheading>
                    {membership.organizationMemberCount} member
                    <span>{membership.organizationMemberCount !== 1 && "s"}</span>
                </Subheading>
            </div>
        </Card>
    );
};
